import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBrandingContext } from '@/contexts/BrandingContext';
import { toast } from 'sonner';

const BRANDING_ID = '00000000-0000-0000-0000-000000000001';

export function useBranding() {
  return useBrandingContext();
}

interface UpdateBrandingData {
  logo_url?: string | null;
  logo_dark_url?: string | null;
  logo_alt_text?: string;
  favicon_url?: string | null;
}

export function useUpdateBranding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateBrandingData) => {
      const { data: user } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('app_branding')
        .update({
          ...data,
          updated_at: new Date().toISOString(),
          updated_by: user.user?.id,
        })
        .eq('id', BRANDING_ID);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-branding'] });
      toast.success('Branding atualizado com sucesso!');
    },
    onError: (error) => {
      console.error('Error updating branding:', error);
      toast.error('Erro ao atualizar branding');
    },
  });
}

export async function uploadBrandingImage(
  file: File,
  type: 'logo' | 'logo-dark' | 'favicon'
): Promise<string> {
  // Validate file type
  const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
  if (!validTypes.includes(file.type)) {
    throw new Error('Tipo de arquivo inválido. Use PNG, JPG ou SVG.');
  }

  // Validate file size (max 2MB)
  if (file.size > 2 * 1024 * 1024) {
    throw new Error('Arquivo muito grande. Máximo 2MB.');
  }

  const fileExt = file.name.split('.').pop();
  const fileName = `branding/${type}-${Date.now()}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from('company-assets')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: true,
    });

  if (uploadError) throw uploadError;

  const { data } = supabase.storage
    .from('company-assets')
    .getPublicUrl(fileName);

  return data.publicUrl;
}
